import {EditorView} from "@codemirror/view";
import {
	AccessorFlags,
	ClassDeclarationInfo,
	GeneralTypeOrAny,
	InterfaceDeclarationInfo,
	LabelInfo,
	LocalVariableDef,
	MemberFieldInfo,
	MethodDeclarationInfo,
	PackageInfo,
	prettyPrintInfo,
	TypeParameterInfo,
	WildcardType,
} from "../type_model";
import {cafebabeAssert, cafebabeUnreachable} from "../error";
import {instantiateType, isClassOfBinaryName} from "../analysis";

export type HoverInfo =
	| PackageInfo
	| MethodDeclarationInfo
	| MemberFieldInfo
	| LocalVariableDef
	| ClassDeclarationInfo
	| InterfaceDeclarationInfo
	| TypeParameterInfo
	| LabelInfo;

const CODE = "12px monospace";
const KEYWORD_COLOR = "#ff8a00";
const IDENT_COLOR = "black";
const TPARAM_COLOR = "blue";

function makeWord(kw: string, color = "#ff8a00") {
	const flagSpan = document.createElement("span");
	flagSpan.innerText = kw;
	flagSpan.style.color = color;
	flagSpan.style.font = CODE;
	return flagSpan;
}

function renderTypeParameter(dom: HTMLElement, info: TypeParameterInfo) {
	dom.appendChild(makeWord(info.name, TPARAM_COLOR));
}

function renderTypeArguments(
	dom: HTMLElement,
	args: (GeneralTypeOrAny | WildcardType)[]
) {
	for (let i = 0; i < args.length; ++i) {
		renderType(dom, args[i]!);
		if (i !== args.length - 1) {
			dom.appendChild(makeWord(", ", IDENT_COLOR));
		}
	}
}

function renderType(dom: HTMLElement, ty: GeneralTypeOrAny | WildcardType) {
	const nowrap = document.createElement("span");
	nowrap.style.whiteSpace = "no-wrap";
	nowrap.style.font = CODE;
	switch (ty.type) {
		case "null":
			break;
		case "primitive":
			nowrap.appendChild(makeWord(ty.primitive, KEYWORD_COLOR));
			break;
		case "intersection":
			break;
		case "raw":
			nowrap.appendChild(makeWord(ty.rawOf.simpleName, IDENT_COLOR));
			break;
		case "nongeneric":
			nowrap.appendChild(makeWord(ty.ty.simpleName, IDENT_COLOR));
			break;
		case "typevar":
			if (ty.typeParameter) renderTypeParameter(nowrap, ty.typeParameter);
			break;
		case "parameterized":
			nowrap.appendChild(makeWord(ty.parameterizes.simpleName, IDENT_COLOR));
			nowrap.appendChild(makeWord("<", IDENT_COLOR));
			renderTypeArguments(nowrap, ty.typeArguments);
			nowrap.appendChild(makeWord(">", IDENT_COLOR));
			break;
		case "array":
			renderType(nowrap, ty.arrayOf);
			nowrap.appendChild(makeWord("[]", IDENT_COLOR));
			break;
		case "any":
			break;
		case "wildcard":
			nowrap.appendChild(makeWord("? ", IDENT_COLOR));
			if (ty.boundKind !== "none") {
				nowrap.appendChild(makeWord(`${ty.boundKind} `, KEYWORD_COLOR));
				if (ty.bound) renderType(dom, ty.bound);
			}
	}

	dom.appendChild(nowrap);
}

function renderPackage(dom: HTMLDivElement, info: PackageInfo) {
	const packageInfo = document.createElement("div");

	const package_ = document.createElement("span");
	package_.innerText = "📦 ";
	package_.style.fontSize = "20px";

	const view = document.createElement("span");
	view.innerText = info.fullyQualifiedName;
	view.style.color = "blue";
	view.style.font = CODE;
	view.style.fontWeight = "bold";
	// center text vertically
	view.style.verticalAlign = "text-top";

	packageInfo.appendChild(package_);
	packageInfo.appendChild(view);

	dom.appendChild(packageInfo);
}

function renderFunction(dom: HTMLDivElement, info: MethodDeclarationInfo) {
	dom.innerHTML = prettyPrintInfo(info);
}

function renderMemberField(dom: HTMLDivElement, info: MemberFieldInfo) {
	dom.innerHTML = prettyPrintInfo(info);
}

function renderLocalVariable(dom: HTMLDivElement, info: LocalVariableDef) {
	dom.innerHTML = prettyPrintInfo(info);
}

function makeLine() {
	const m = document.createElement("div");
	m.style.display = "flex";
	m.style.flexDirection = "row";
	m.style.alignItems = "center";
	m.style.gap = "5px";
	m.style.margin = "5px 0";
	return m;
}

function renderTypeDeclaration(
	dom: HTMLDivElement,
	info: InterfaceDeclarationInfo | ClassDeclarationInfo
) {
	if (info.package) {
		renderPackage(dom, info.package);
	}

	const filteredFlags =
		info.flags &
		~(info.type === "interface"
			? AccessorFlags.abstract | AccessorFlags.static | AccessorFlags.final
			: 0);

	const flagOrder: string[] = [
		"public",
		"protected",
		"private",
		"abstract",
		"static",
		"final",
	];

	const modifiersAndClass = makeLine();
	const implements_ = makeLine();

	for (const flag of flagOrder) {
		if (filteredFlags & (AccessorFlags[flag as any] as any)) {
			modifiersAndClass.appendChild(makeWord(flag));
		}
	}

	modifiersAndClass.appendChild(makeWord(info.type));
	renderType(modifiersAndClass, instantiateType(info));

	const extendsClause = makeLine();
	if (info.extends) {
		let extendsList = Array.isArray(info.extends)
			? info.extends
			: [info.extends];
		extendsList = extendsList.filter(
			(e) => !isClassOfBinaryName(e, "java.lang.Object")
		);
		if (extendsList.length > 0) {
			extendsClause.appendChild(makeWord("extends"));
			renderTypeArguments(extendsClause, extendsList);
		}
	}
	if (info.type === "class" && info.implements) {
		const implementsList = info.implements;
		if (implementsList.length > 0) {
			extendsClause.appendChild(makeWord("implements"));
			renderTypeArguments(implements_, implementsList);
		}
	}

	dom.appendChild(modifiersAndClass);
	dom.appendChild(extendsClause);
	dom.appendChild(implements_);
}

/**
 * Result of hovering over a part of the code.
 */
export class HoverResult {
	private constructor(
		public readonly info: HoverInfo | null,
		public readonly string: string | null = null
	) {}

	static string(s: string) {
		return new HoverResult(null, s);
	}

	static info(info: HoverInfo) {
		return new HoverResult(info);
	}

	makeNode(view: EditorView) {
		const dom = document.createElement("div");

		if (this.info) {
			switch (this.info.type) {
				case "tparam":
					renderTypeParameter(dom, this.info);
					break;
				case "package":
					renderPackage(dom, this.info);
					break;
				case "function":
					renderFunction(dom, this.info);
					break;
				case "member":
					renderMemberField(dom, this.info);
					break;
				case "local":
					renderLocalVariable(dom, this.info);
					break;
				case "class":
				case "interface":
					renderTypeDeclaration(dom, this.info);
					break;
				default:
					cafebabeUnreachable("Cannot render this information");
			}
			dom.style.flexDirection = "column";
			dom.style.display = "flex";
		} else {
			cafebabeAssert(this.string != null, "No information to render");
			dom.innerHTML = this.string;
		}

		dom.style.maxWidth = "400px";
		dom.style.maxHeight = "400px";
		dom.style.overflow = "auto";
		dom.style.padding = "10px";

		return dom;
	}
}
